// @ts-nocheck
import { formatStatusLabel } from "../../helpers/statusStyles";

import { DashboardLayout } from "@layouts/index";
import notification from "../../assets/images/notifications.png";
import { useEffect, useRef, useState } from "react";
import {
  getAllPropertyTransactions,
  getBookingsSummary,
} from "@services/krent.service";
import { useSessionStorage } from "../../hooks";
import Table from "@shared/Table/Table";
import TableSkeleton from "@shared/Table/TableSkeleton";
import ktoast from "@services/toast.service";
import Tabs from "@shared/Tabs";
import Pagination from "components/Pagination";
import KycBanner from "components/KycBanner";
import customFetch from "@services/axio";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";

type Intent = "shortlet" | "long lease" | "sale" | "ibuying" | "all";

interface Bill {
  total: string;
  [key: string]: any;
}

interface Transaction {
  id: string;
  property: string;
  owner: string;
  intent: Intent;
  arrivalDate: string;
  departureDate: string;
  inspected: boolean;
  updatedAt: string;
  createdAt: string;
  status: string;
  bill: Bill[];
  listing: {
    intent: Intent;
    [key: string]: any;
  };
  [key: string]: any;
}

interface User {
  id: string;
  [key: string]: any;
}

interface TabComponents {
  Shortlet: JSX.Element;
  Rent: JSX.Element;
  // Sale: JSX.Element;
  // iBuying: JSX.Element;
}

export const MyBookings = () => {
  const [user] = useSessionStorage("user", {} as User);
  const [loading, setLoading] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<
    Transaction[]
  >([]);
  const [page, setPage] = useState<number>(1);

  const [pendingPayments, setPendingPayments] = useState<number>(0);
  const [completedPayments, setCompletedPayments] = useState<number>(0);
  const [totalPayments, setTotalPayments] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTimeline =
    searchParams.get("tab") === "rent" ? "Rent" : "Shortlet";
  const [pagination, setPagination] = useState({});
  const [userKyc, setUserKyc] = useState();
  const [loadingKyc, setLoadingKyc] = useState(false);
  const intentMap: Record<string, string> = {
    Shortlet: "shortlet",
    Rent: "long lease",
    Sale: "sale",
    iBuying: "ibuying",
  };

  const filterTransactionsByTimeline = (
    allTransactions: Transaction[],
    timeline: string,
  ) => {
    const intentValue = intentMap[timeline];
    if (intentValue && allTransactions?.length > 0) {
      return allTransactions
        .filter((t) => t.type === "initial")
        .filter(
          (t) => t.listing.intent?.toLowerCase() === intentValue.toLowerCase(),
        );
    }
    return [];
  };

  useEffect(() => {
    const getPersonalKycStatus = async () => {
      setLoadingKyc(true);
      try {
        const resp = await customFetch.get("/users/kyc?type=PERSONAL");
        setUserKyc(resp?.data?.data);
      } catch (error: any) {}
      setLoadingKyc(false);
    };

    getPersonalKycStatus();
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    const abortController = new AbortController();
    const intent = intentMap[currentTimeline];

    setLoading(true);

    getAllPropertyTransactions(user?.id, abortController.signal, page, intent)
      .then((res: any) => {
        setTransactions(res.data);
        setPagination(res.pagination || { last_page: 1 });
      })
      .catch((err: any) => ktoast.error(err?.errors?.[0]?.message))
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [page, currentTimeline, user?.id]);

  useEffect(() => {
    const abortController = new AbortController();

    getBookingsSummary(abortController.signal)
      .then((res: any) => {
        setPendingPayments(res.data?.pendingPayments || 0);
        setCompletedPayments(res.data?.completedPayments || 0);
        setTotalPayments(res.data?.totalPayments || 0);
      })
      .catch((err: any) => {
        console.error("Error fetching bookings summary:", err);
      });

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    setFilteredTransactions(
      filterTransactionsByTimeline(transactions, currentTimeline),
    );
  }, [currentTimeline, transactions]);

  const handleTabChange = (tabName: string): void => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tabName === "Rent" ? "rent" : "shortlet");
    setSearchParams(nextParams, { replace: true });
    setPage(1);
  };

  const summaryItems = [
    {
      label: "Pending Payments",
      value: pendingPayments,
      showTooltip: true,
    },
    {
      label: "Completed Payments",
      value: completedPayments,
      showTooltip: false,
    },
    {
      label: "Total Payments",
      value: totalPayments,
      showTooltip: false,
    },
  ];

  const navigate = useNavigate();
  const [rentSearch, setRentSearch] = useState("");
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const actionTriggerRef = useRef<HTMLButtonElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!openActionId) return;
    const handleOutside = (e: MouseEvent) => {
      if (actionMenuRef.current?.contains(e.target as Node)) return;
      if (actionTriggerRef.current?.contains(e.target as Node)) return;
      setOpenActionId(null);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [openActionId]);

  const openAction = (id: string, btn: HTMLButtonElement) => {
    const rect = btn.getBoundingClientRect();
    actionTriggerRef.current = btn;
    setActionMenuPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
    setOpenActionId(id);
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatAmount = (bill: any[]) => {
    if (!bill || !Array.isArray(bill)) return "—";
    const initial = bill.find((b: any) => b.total);
    return initial?.total ? `₦${Number(initial.total).toLocaleString()}` : "—";
  };

  const getRentStatusBadge = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "confirmed" || s === "customer-accepted")
      return { label: "Active", bg: "#d1fae5", color: "#065f46" };
    if (s === "completed")
      return { label: "Ended", bg: "#fee2e2", color: "#991b1b" };
    if (s === "cancelled")
      return { label: "Cancelled", bg: "#fee2e2", color: "#991b1b" };
    if (s === "pending_payment")
      return { label: "Pending", bg: "#dbeafe", color: "#1e40af" };
    return {
      label: formatStatusLabel(status) || "Pending",
      bg: "#f3f4f6",
      color: "#374151",
    };
  };

  const rentFiltered = transactions.filter(
    (t: any) =>
      !rentSearch ||
      t.listing?.title?.toLowerCase().includes(rentSearch.toLowerCase()) ||
      (t.owner?.preferredName || t.owner?.firstname || "")
        .toLowerCase()
        .includes(rentSearch.toLowerCase()),
  );

  const renderRentTable = () => {
    if (loading) {
      return (
        <div
          className="rounded-3 overflow-hidden"
          style={{ border: "1px solid #e5e7eb" }}
        >
          <TableSkeleton columns={7} rows={5} />
        </div>
      );
    }

    return (
      <>
        {/* Search + Filter bar */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <div
            className="d-flex align-items-center flex-grow-1 px-3 rounded-3"
            style={{
              border: "1px solid #dee2e6",
              height: "44px",
              background: "#fff",
            }}
          >
            <i
              className="fa-regular fa-magnifying-glass text-muted me-2"
              style={{ fontSize: "14px" }}
            ></i>
            <input
              type="text"
              className="border-0 flex-grow-1 font-onest fs-14"
              placeholder="Search property"
              style={{ outline: "none", background: "transparent" }}
              value={rentSearch}
              onChange={(e) => setRentSearch(e.target.value)}
            />
          </div>
          <button
            className="d-flex align-items-center gap-2 px-4 font-onest fw-500 fs-14 rounded-3"
            style={{
              height: "44px",
              border: "1px solid #dee2e6",
              background: "#fff",
              whiteSpace: "nowrap",
            }}
          >
            <i className="fa-regular fa-sliders text-muted"></i>
            Filters
          </button>
        </div>

        {rentFiltered.length === 0 ? (
          <div className="card border-0 p-5 text-center">
            <div>
              <h6 className="fs-18 mt-1 fw-bold mb-4">No rent bookings yet</h6>
              <img src={notification} alt="No transactions" width={200} />
              <p className="fs-14 light-text mt-3">
                Any rent transactions you make would show up here.
              </p>
            </div>
          </div>
        ) : (
          <div
            className="rounded-3 overflow-hidden"
            style={{ border: "1px solid #e5e7eb" }}
          >
            <div className="table-responsive">
              <table className="table mb-0" style={{ minWidth: "860px" }}>
                <thead style={{ borderBottom: "2px solid #e5e7eb" }}>
                  <tr>
                    {[
                      "Rent Date",
                      "Property Title",
                      "Landlord",
                      "Rent End Date",
                      "Rent fee",
                      "Rent Status",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 font-onest fw-600 fs-13"
                        style={{
                          color: "#374151",
                          background: "#fff",
                          whiteSpace: "nowrap",
                          borderBottom: "none",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rentFiltered.map((info: any, idx: number) => {
                    const badge = getRentStatusBadge(info.status);
                    const isConfirmed =
                      info.status === "confirmed" ||
                      info.status === "customer-accepted";
                    return (
                      <tr
                        key={info.id}
                        style={{
                          borderTop: idx === 0 ? "none" : "1px solid #f3f4f6",
                          background: "#fff",
                        }}
                      >
                        <td
                          className="px-4 py-4 font-onest fs-13"
                          style={{ color: "#374151", whiteSpace: "nowrap" }}
                        >
                          {info.arrival_date
                            ? formatDate(info.arrival_date)
                            : "—"}
                        </td>
                        <td
                          className="px-4 py-4 font-onest fs-13"
                          style={{ color: "#374151", maxWidth: "220px" }}
                        >
                          {info.listing?.title || "—"}
                        </td>
                        <td
                          className="px-4 py-4 font-onest fs-13"
                          style={{ color: "#374151" }}
                        >
                          {info.owner?.preferredName ||
                            info.owner?.firstname ||
                            "—"}
                        </td>
                        <td
                          className="px-4 py-4 font-onest fs-13"
                          style={{ color: "#374151", whiteSpace: "nowrap" }}
                        >
                          {info.departure_date
                            ? formatDate(info.departure_date)
                            : "—"}
                        </td>
                        <td
                          className="px-4 py-4 font-onest fs-13 fw-semibold"
                          style={{ color: "#374151" }}
                        >
                          {formatAmount(info.bill)}
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className="rounded-pill px-3 py-1 font-onest fs-12 fw-500"
                            style={{
                              background: badge.bg,
                              color: badge.color,
                              display: "inline-block",
                            }}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={(e) =>
                              openAction(info.id, e.currentTarget)
                            }
                          >
                            <i className="fa-solid fa-ellipsis-vertical text-primary fs-16"></i>
                          </button>

                          {openActionId === info.id &&
                            actionMenuPos &&
                            createPortal(
                              <div
                                ref={actionMenuRef}
                                style={{
                                  position: "fixed",
                                  top: actionMenuPos.top,
                                  right: actionMenuPos.right,
                                  zIndex: 2000,
                                  background: "#fff",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "10px",
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                  minWidth: "190px",
                                  padding: "6px 0",
                                }}
                              >
                                <button
                                  className="dropdown-item d-flex align-items-center gap-2 font-onest fs-13 py-2 px-3"
                                  onClick={() => {
                                    setOpenActionId(null);
                                    navigate(`/my-bookings/${info.id}`);
                                  }}
                                >
                                  <i
                                    className="fa-regular fa-magnifying-glass"
                                    style={{
                                      color: "#374151",
                                      fontSize: "14px",
                                    }}
                                  ></i>
                                  Preview property
                                </button>
                                <button
                                  className="dropdown-item d-flex align-items-center gap-2 font-onest fs-13 py-2 px-3"
                                  disabled={!isConfirmed}
                                  style={{
                                    opacity: isConfirmed ? 1 : 0.4,
                                    cursor: isConfirmed
                                      ? "pointer"
                                      : "not-allowed",
                                  }}
                                  onClick={() => {
                                    if (!isConfirmed) return;
                                    setOpenActionId(null);
                                    navigate(`/my-bookings/${info.id}`);
                                  }}
                                >
                                  <i
                                    className="fa-regular fa-receipt"
                                    style={{
                                      color: "#374151",
                                      fontSize: "14px",
                                    }}
                                  ></i>
                                  View receipt
                                </button>
                                {/* <button
                                  className="dropdown-item d-flex align-items-center gap-2 font-onest fs-13 py-2 px-3"
                                  disabled={!isConfirmed}
                                  style={{
                                    opacity: isConfirmed ? 1 : 0.4,
                                    cursor: isConfirmed
                                      ? "pointer"
                                      : "not-allowed",
                                  }}
                                  onClick={() => {
                                    if (!isConfirmed) return;
                                    setOpenActionId(null);
                                    navigate(`/my-bookings/${info.id}/renew`);
                                  }}
                                >
                                  <i
                                    className="fa-regular fa-rotate-right"
                                    style={{
                                      color: "#374151",
                                      fontSize: "14px",
                                    }}
                                  ></i>
                                  Renew rent
                                </button> */}
                              </div>,
                              document.body,
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination inside the table box, right-aligned */}
            <div className="d-flex justify-content-end px-4 py-3">
              <Pagination
                page={page}
                numOfPages={(pagination as any).last_page || 1}
                setPage={setPage}
              />
            </div>
          </div>
        )}
      </>
    );
  };

  const components: TabComponents = {
    Shortlet: (
      <Table
        data={filteredTransactions}
        th={[
          "Booking Type",
          "Booking date",
          "Property",
          "owner",
          "Arrival Date",
          "Departure Date",
          "Updated date",
          "status",
          "Actions",
        ]}
        transactions
        getTransactions={() => {}}
        page={page}
        numOfPages={(pagination as any).last_page || 1}
        setPage={setPage}
        loading={loading}
      />
    ),
    Rent: renderRentTable(),
    // Sale: (
    //   <Table
    //     data={filteredTransactions}
    //     th={[
    //       "Booking Type",
    //       "Booking date",
    //       "Property",
    //       "owner",
    //       "Arrival Date",
    //       "Departure Date",
    //       "inspected",
    //       "Updated date",
    //       "status",
    //       "Actions",
    //     ]}
    //     transactions
    //     getTransactions={() => {}}
    //     page={page}
    //     numOfPages={(pagination as any).last_page || 1}
    //     setPage={setPage}
    //     loading={loading}
    //   />
    // ),
    // iBuying: (
    //   <Table
    //     data={filteredTransactions}
    //     th={[
    //       "Booking Type",
    //       "Booking date",
    //       "Property",
    //       "owner",
    //       "Arrival Date",
    //       "Departure Date",
    //       "inspected",
    //       "Updated date",
    //       "status",
    //       "Actions",
    //     ]}
    //     transactions
    //     getTransactions={() => {}}
    //     page={page}
    //     numOfPages={(pagination as any).last_page || 1}
    //     setPage={setPage}
    //     loading={loading}
    //   />
    // ),
  };

  return (
    <DashboardLayout
      title="My Bookings"
      subtitle="Manage your bookings on krent"
    >
      <KycBanner
        loading={loadingKyc}
        show={!userKyc}
        title="Please complete your KYC before checking in"
      />

      <div className="row my-4">
        {summaryItems.map((item, index) => (
          <div key={index} className="col-12 col-md-6 col-lg-4 mb-3 mb-md-0">
            <div
              className="rounded-4 px-4 py-5"
              style={{
                backgroundColor: "#FAFAFA",
                border: "1px solid #EEEEEE",
              }}
            >
              <h6 className="fs-14 mt-1">{item.label} </h6>
              <h3 className="fw-bold">{item.value?.toLocaleString()}</h3>
            </div>
          </div>
        ))}
      </div>

      <div className="row mt-5 mt-md-4">
        <div className="col-12 mb-5 mb-md-0">
          <div className="flex gap-2 items-center mb-4">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.333 14.9167C15.333 13.9958 12.7213 13.25 9.49967 13.25M15.333 14.9167C15.333 15.8375 12.7213 16.5833 9.49967 16.5833C6.27801 16.5833 3.66634 15.8375 3.66634 14.9167M15.333 14.9167V19.0308C15.333 19.98 12.7213 20.75 9.49967 20.75C6.27801 20.75 3.66634 19.9808 3.66634 19.0308V14.9167M15.333 14.9167C18.5197 14.9167 21.1663 14.0942 21.1663 13.25V4.91667M9.49967 13.25C6.27801 13.25 3.66634 13.9958 3.66634 14.9167M9.49967 13.25C5.81801 13.25 2.83301 12.4275 2.83301 11.5833V7.41667M9.49967 5.75C5.81801 5.75 2.83301 6.49583 2.83301 7.41667M2.83301 7.41667C2.83301 8.3375 5.81801 9.08333 9.49967 9.08333C9.49967 9.9275 12.2105 10.75 15.3972 10.75C18.583 10.75 21.1663 9.9275 21.1663 9.08333M21.1663 4.91667C21.1663 3.99583 18.583 3.25 15.3972 3.25C12.2105 3.25 9.62801 3.99583 9.62801 4.91667M21.1663 4.91667C21.1663 5.8375 18.583 6.58333 15.3972 6.58333C12.2113 6.58333 9.62801 5.8375 9.62801 4.91667M9.62801 4.91667V13.3883"
                stroke="#727272"
                strokeWidth="1.5"
              />
            </svg>
            <h6 className="fw-500 fs-16 " style={{ color: "#727272" }}>
              My Bookings
            </h6>
          </div>
          <Tabs
            components={components}
            onTabChange={handleTabChange}
            activeIndex={Object.keys(components).indexOf(currentTimeline)}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyBookings;
